import { ChevronLeft, Xmark } from '@gravity-ui/icons';
import { Button } from '@heroui/react';
import classNames from 'classnames';
import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDebouncedLoader } from 'use-debounced-loader';

import { pendingKeepsAppearanceClass } from '../Button';
import FlipOnRtl from '../FlipOnRtl';
import MatrixLoader from '../MatrixLoader';

type Props = {
  readonly title?: string;
  readonly type?: 'back' | 'close';
  readonly isHidden?: boolean;
  readonly onClose?: () => void;
  readonly onBack?: () => void;
  /** An async skip keeps the button pending until it settles; a final redirect never does. */
  readonly onSkip?: () => Promise<void> | void;
};

const NavBar = ({ title, type = 'back', isHidden, onClose, onBack, onSkip }: Props) => {
  const { t } = useTranslation();
  const [isSkipping, setIsSkipping] = useState(false);
  const isSkipLoaderVisible = useDebouncedLoader(isSkipping, 200);

  const handleSkip = useCallback(async () => {
    if (!onSkip) {
      return;
    }

    setIsSkipping(true);

    try {
      await onSkip();
    } finally {
      setIsSkipping(false);
    }
  }, [onSkip]);

  const isClosable = type === 'close';
  const handleBack = useCallback(() => {
    if (onBack) {
      onBack();

      return;
    }

    window.history.back();
  }, [onBack]);

  const pressHandler = useCallback(() => {
    if (onClose) {
      onClose();

      return;
    }

    if (isClosable) {
      window.close();

      return;
    }

    handleBack();
  }, [handleBack, isClosable, onClose]);

  return (
    <nav
      // Mobile keeps the row's height when hidden so the title doesn't jump between steps.
      className="relative mb-4 flex min-h-11 w-full items-center justify-center md:min-h-10 desktop:-mt-3 mobile:data-hidden:invisible desktop:data-hidden:hidden"
      // `||` drops the attribute entirely when isHidden is false.
      // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing
      data-hidden={isHidden || undefined}
    >
      {/* The wrappers carry the test ids: integration tests select `div[data-testid=…]`. */}
      {/* The inset cancels the button padding and the chevron's side bearing: the glyph aligns with the title. */}
      <div data-testid="nav-back" className="absolute inset-s-0 -ms-4.5 md:-ms-4">
        <Button isIconOnly variant="ghost" size="lg" onPress={pressHandler}>
          {isClosable ? (
            <Xmark />
          ) : (
            <FlipOnRtl>
              <ChevronLeft />
            </FlipOnRtl>
          )}
          <span className="sr-only">{t(isClosable ? 'action.cancel' : 'action.nav_back')}</span>
        </Button>
      </div>
      {title && (
        <div className="max-w-[60%] truncate text-base font-semibold text-foreground" title={title}>
          {title}
        </div>
      )}
      {onSkip && (
        <div data-testid="nav-skip" className="absolute inset-e-0 -me-4">
          <Button
            variant="ghost"
            size="lg"
            className={classNames('desktop:text-sm', pendingKeepsAppearanceClass)}
            isPending={isSkipping}
            onPress={handleSkip}
          >
            {isSkipLoaderVisible && <MatrixLoader size="sm" />}
            {t('action.nav_skip')}
          </Button>
        </div>
      )}
    </nav>
  );
};

export default NavBar;
